import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Offcanvas } from 'bootstrap';
import { 
  FiMail, 
  FiChevronRight, 
  FiArrowLeft, 
  FiCopy, 
  FiCheck, 
  FiExternalLink,
  FiPlus
} from 'react-icons/fi';
import { 
  FaFacebook, 
  FaInstagram, 
  FaLinkedinIn, 
  FaXTwitter,
  FaWhatsapp
} from 'react-icons/fa6';
import { BsChatDots, BsTicketDetailed } from 'react-icons/bs';
import { HiClipboardDocumentList } from 'react-icons/hi2';
import { Search } from 'lucide-react';
import { createSupportTicket, getSupportTickets } from '../../actions/user';
import Pagination from '../Pagination/Pagination';

const Support = ({ userDetails }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('contact'); // 'contact' | 'tickets'
  const [copiedEmail, setCopiedEmail] = useState(false);

  // Tickets state
  const [searchQuery, setSearchQuery] = useState('');
  const [ticketList, setTicketList] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('low');
  const [attachment, setAttachment] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0
  });

  const offcanvasRef = useRef(null);

  // Synchronize user with Tawk.to
  useEffect(() => {
    if (window.Tawk_API && userDetails) {
      const setAttrs = () => {
        if (typeof window.Tawk_API.setAttributes === 'function') {
          window.Tawk_API.setAttributes({
            name: userDetails.full_name || (userDetails.first_name ? `${userDetails.first_name} ${userDetails.last_name}` : 'User'),
            email: userDetails.email || '',
          }, function() {});
        }
      };
      if (window.Tawk_API.isChatMaximized) {
        setAttrs();
      } else {
        window.Tawk_API.onLoad = setAttrs;
        setAttrs();
      }
    }
  }, [userDetails]);

  // Open Tawk.to live chat
  const handleOpenLiveChat = () => {
    if (window.Tawk_API && typeof window.Tawk_API.maximize === 'function') {
      window.Tawk_API.maximize();
    } else {
      toast.info("Connecting to live chat support...", {
        toastId: "tawk-connecting",
        autoClose: 2000
      });
    }
  };

  // Open email app
  const handleOpenEmail = () => {
    window.location.href = "mailto:support@cardplot.com?subject=Support%20Request%20-%20Cardplot";
  };

  // Copy support email
  const handleCopyEmail = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText("support@cardplot.com");
    setCopiedEmail(true);
    toast.success("Support email copied to clipboard!");
    setTimeout(() => setCopiedEmail(false), 2500);
  };

  const handleOpenOffcanvas = () => {
    if (offcanvasRef.current) {
      const bsOffcanvas = new Offcanvas(offcanvasRef.current);
      bsOffcanvas.show();
    }
  };

  const handleNewTicketSubmit = async (e) => {
    e.preventDefault();
    if (!title || !description || !priority) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsFormLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("priority", priority);
      formData.append("description", description);
      if (attachment) formData.append("attachment", attachment);

      await createSupportTicket(formData);
      toast.success("Ticket submitted successfully!");

      setTitle('');
      setDescription('');
      setPriority('low');
      setAttachment(null);
      e.target.reset();

      const bsOffcanvas = Offcanvas.getInstance(offcanvasRef.current);
      bsOffcanvas?.hide();

      fetchTickets();
    } catch (error) {
      const responseErrors = error?.response?.data?.errors;
      const fallbackMessage = error?.response?.data?.message || "Error Creating Support Ticket";

      if (responseErrors && Object.keys(responseErrors).length > 0) {
        const errorMessages = Object.values(responseErrors).flat().filter(Boolean);
        toast.error(errorMessages.length > 1 ? errorMessages[0] : errorMessages.join("<br/>"));
      } else {
        toast.error(fallbackMessage);
      }
    } finally {
      setIsFormLoading(false);
    }
  };

  const fetchTickets = async (pageNum = page) => {
    setTicketLoading(true);
    try {
      const filters = {
        query: searchQuery,
        paginate: {
          per_page: perPage,
          current_page: pageNum
        }
      };

      const response = await getSupportTickets(filters);
      const tickets = response.data?.data?.support_tickets;

      setTicketList(tickets?.data || []);
      setPagination({
        current_page: tickets?.current_page || 1,
        last_page: tickets?.last_page || 1,
        total: tickets?.total || 0
      });
    } catch (error) {
      // console.error("Error fetching tickets:", error);
    } finally {
      setTicketLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchTickets();
    }
  }, [activeTab, page, searchQuery]);

  return (
    <section className="contact__support__page py-3 px-2 px-md-4" style={{ minHeight: '80vh' }}>
      <div className="mx-auto" style={{ maxWidth: '640px' }}>
        
        {/* Top Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <button 
            type="button" 
            onClick={() => {
              if (activeTab === 'tickets') {
                setActiveTab('contact');
              } else {
                navigate(-1);
              }
            }}
            className="btn btn-link text-decoration-none text-dark p-0 d-flex align-items-center gap-2"
            style={{ fontWeight: 600, fontSize: '18px' }}
          >
            <FiArrowLeft size={20} />
            <span>{activeTab === 'tickets' ? 'Support Tickets' : 'Contact Support'}</span>
          </button>

          {activeTab === 'tickets' && (
            <button
              className="btn btn-sm primary__btn d-flex gap-2 align-items-center px-3 py-2 rounded-pill"
              onClick={handleOpenOffcanvas}
            >
              <FiPlus />
              <span>Open Ticket</span>
            </button>
          )}
        </div>

        {/* VIEW 1: Contact Hub (Matching Screenshot) */}
        {activeTab === 'contact' && (
          <div className="d-flex flex-column gap-4">
            
            {/* Section 1: Chat */}
            <div>
              <h6 className="text-muted fw-semibold mb-3" style={{ fontSize: '14px' }}>
                Chat
              </h6>
              <div className="d-flex flex-column gap-3">
                {/* Live Chat Card */}
                <div 
                  onClick={handleOpenLiveChat}
                  className="bg-white rounded-4 p-3 d-flex align-items-center justify-content-between shadow-sm border border-light-subtle"
                  style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ 
                        width: '46px', 
                        height: '46px', 
                        backgroundColor: '#F3EEFF', 
                        color: '#7C3AED' 
                      }}
                    >
                      <BsChatDots size={22} />
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold text-dark" style={{ fontSize: '15px' }}>
                        Live Chat
                      </h6>
                      <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                        Start instant conversation on live chat
                      </p>
                    </div>
                  </div>
                  <FiChevronRight size={18} className="text-muted flex-shrink-0" />
                </div>

                {/* WhatsApp Chat Card */}
                <a 
                  href="https://wa.me/447347949022?text=Hello%20CardPlot%20Support" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-4 p-3 d-flex align-items-center justify-content-between shadow-sm border border-light-subtle text-decoration-none"
                  style={{ transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ 
                        width: '46px', 
                        height: '46px', 
                        backgroundColor: '#EBFBF2', 
                        color: '#25D366' 
                      }}
                    >
                      <FaWhatsapp size={24} color="#25D366" />
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold text-dark" style={{ fontSize: '15px' }}>
                        WhatsApp
                      </h6>
                      <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                        Chat directly with our support team
                      </p>
                    </div>
                  </div>
                  <FiChevronRight size={18} className="text-muted flex-shrink-0" />
                </a>

                {/* Email Card */}
                <div 
                  onClick={handleOpenEmail}
                  className="bg-white rounded-4 p-3 d-flex align-items-center justify-content-between shadow-sm border border-light-subtle"
                  style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ 
                        width: '46px', 
                        height: '46px', 
                        backgroundColor: '#F3EEFF', 
                        color: '#7C3AED' 
                      }}
                    >
                      <FiMail size={22} />
                    </div>
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <h6 className="mb-1 fw-bold text-dark" style={{ fontSize: '15px' }}>
                          Email
                        </h6>
                        <span 
                          onClick={handleCopyEmail}
                          className="badge bg-light text-primary border d-inline-flex align-items-center gap-1"
                          style={{ fontSize: '10px', cursor: 'pointer', padding: '2px 6px' }}
                          title="Copy support@cardplot.com"
                        >
                          {copiedEmail ? <><FiCheck /> Copied</> : <><FiCopy /> Copy support@cardplot.com</>}
                        </span>
                      </div>
                      <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                        We aim to respond in a day
                      </p>
                    </div>
                  </div>
                  <FiChevronRight size={18} className="text-muted flex-shrink-0" />
                </div>
              </div>
            </div>
             {/* Section 3: Support Tickets link */}
            <div>
              <h6 className="text-muted fw-semibold mb-3" style={{ fontSize: '14px' }}>
                Support
              </h6>
              <div 
                onClick={() => setActiveTab('tickets')}
                className="bg-white rounded-4 p-3 d-flex align-items-center justify-content-between shadow-sm border border-light-subtle"
                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div 
                    className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '44px', height: '44px', backgroundColor: '#F0F9FF', color: '#0A95FF' }}
                  >
                    <BsTicketDetailed size={22} />
                  </div>
                  <div>
                    <h6 className="mb-1 fw-bold text-dark" style={{ fontSize: '15px' }}>
                      Support Tickets
                    </h6>
                    <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                      View your opened support tickets or track history
                    </p>
                  </div>
                </div>
                <FiChevronRight size={18} className="text-muted" />
              </div>
            </div>

            {/* Section 2: Social media */}
            <div>
              <h6 className="text-muted fw-semibold mb-3" style={{ fontSize: '14px' }}>
                Social media
              </h6>
              <div className="d-flex flex-column gap-3">
                {/* Facebook */}
                <a 
                  href="https://facebook.com/usecardplot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-4 p-3 d-flex align-items-center justify-content-between shadow-sm border border-light-subtle text-decoration-none"
                  style={{ transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '44px', height: '44px', backgroundColor: '#F0F6FF' }}
                    >
                      <FaFacebook size={24} color="#1877F2" />
                    </div>
                    <span className="fw-semibold text-dark" style={{ fontSize: '15px' }}>
                      Facebook
                    </span>
                  </div>
                  <FiChevronRight size={18} className="text-muted" />
                </a>

                {/* Instagram */}
                <a 
                  href="https://instagram.com/usecardplot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-4 p-3 d-flex align-items-center justify-content-between shadow-sm border border-light-subtle text-decoration-none"
                  style={{ transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '44px', height: '44px', backgroundColor: '#FFF0F5' }}
                    >
                      <FaInstagram size={24} color="#E4405F" />
                    </div>
                    <span className="fw-semibold text-dark" style={{ fontSize: '15px' }}>
                      Instagram
                    </span>
                  </div>
                  <FiChevronRight size={18} className="text-muted" />
                </a>

                {/* LinkedIn */}
                <a 
                  href="https://linkedin.com/company/cardplot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-4 p-3 d-flex align-items-center justify-content-between shadow-sm border border-light-subtle text-decoration-none"
                  style={{ transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '44px', height: '44px', backgroundColor: '#F0F7FF' }}
                    >
                      <FaLinkedinIn size={22} color="#0A66C2" />
                    </div>
                    <span className="fw-semibold text-dark" style={{ fontSize: '15px' }}>
                      LinkedIn
                    </span>
                  </div>
                  <FiChevronRight size={18} className="text-muted" />
                </a>

                {/* Twitter */}
                <a 
                  href="https://x.com/usecardplot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-4 p-3 d-flex align-items-center justify-content-between shadow-sm border border-light-subtle text-decoration-none"
                  style={{ transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '44px', height: '44px', backgroundColor: '#F8F9FA' }}
                    >
                      <FaXTwitter size={20} color="#111111" />
                    </div>
                    <span className="fw-semibold text-dark" style={{ fontSize: '15px' }}>
                      Twitter
                    </span>
                  </div>
                  <FiChevronRight size={18} className="text-muted" />
                </a>

                {/* WhatsApp */}
                <a 
                  href="https://whatsapp.com/channel/0029Vb79EKfD38COSUr8Qi42" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-4 p-3 d-flex align-items-center justify-content-between shadow-sm border border-light-subtle text-decoration-none"
                  style={{ transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ width: '44px', height: '44px', backgroundColor: '#EBFBF2' }}
                    >
                      <FaWhatsapp size={24} color="#25D366" />
                    </div>
                    <span className="fw-semibold text-dark" style={{ fontSize: '15px' }}>
                      WhatsApp Chennel
                    </span>
                  </div>
                  <FiChevronRight size={18} className="text-muted" />
                </a>
              </div>
            </div>

           
          </div>
        )}

        {/* VIEW 2: Support Tickets Listing */}
        {activeTab === 'tickets' && (
          <div>
            <div className="mb-3">
              <div className="input-group shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <span className="input-group-text bg-white border-end-0">
                  <Search size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search tickets by title or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ fontSize: '14px' }}
                />
              </div>
            </div>

            {ticketLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : ticketList.length > 0 ? (
              <>
                <div className="d-flex flex-column gap-3 mt-3">
                  {ticketList.map((ticket) => (
                    <div 
                      key={ticket.id} 
                      className="card border-0 shadow-sm rounded-4 p-3" 
                      onClick={() => navigate(`/user/admin_support/${ticket.id}`)} 
                      role="button"
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      <div className="card-body p-0">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0 fw-bold text-dark">{ticket.title}</h6>
                          <span className={`badge rounded-pill px-2 py-1 bg-${ticket.priority === 'high' ? 'danger' : ticket.priority === 'medium' ? 'warning' : 'secondary'}`}>
                            {ticket.priority?.toUpperCase()}
                          </span>
                        </div>
                        <p className="mb-2 text-muted text-truncate" style={{ fontSize: '0.85rem' }}>{ticket.description}</p>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">Ticket ID: #{ticket.id}</small>
                          <small className="text-muted">{new Date(ticket.created_at).toLocaleDateString()}</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="d-flex justify-content-end my-4">
                  <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.last_page}
                    onPageChange={(pageNum) => {
                      setPage(pageNum);
                      fetchTickets(pageNum);
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="d-flex flex-column justify-content-center align-items-center py-5 text-center bg-white rounded-4 shadow-sm border mt-3">
                <HiClipboardDocumentList size={60} color="#0A95FF" className="mb-2" />
                <h5 className="fw-bold mb-1">No Tickets Found</h5>
                <p className="text-muted small mb-3">You haven't submitted any support tickets yet.</p>
                <button 
                  className="btn btn-sm primary__btn px-3 py-2 rounded-pill"
                  onClick={handleOpenOffcanvas}
                >
                  <FiPlus className="me-1" /> Create First Ticket
                </button>
              </div>
            )}
          </div>
        )}

      </div>

      {/* New Ticket Offcanvas Drawer */}
      <div
        ref={offcanvasRef}
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="ticketOffcanvasRight"
        aria-labelledby="ticketOffcanvasRightLabel"
        style={{ width: '380px', maxWidth: '100vw' }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title fw-bold" id="ticketOffcanvasRightLabel">Open Support Ticket</h5>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>

        <div className="offcanvas-body p-4">
          <form onSubmit={handleNewTicketSubmit} className="d-flex flex-column gap-3">
            <div>
              <label className="form-label fw-semibold small text-muted">Subject / Title *</label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Brief summary of your issue"
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                required
              />
            </div>

            <div>
              <label className="form-label fw-semibold small text-muted">Priority *</label>
              <select className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)} required>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label className="form-label fw-semibold small text-muted">Description *</label>
              <textarea 
                className="form-control" 
                rows="4" 
                placeholder="Explain the issue in detail..."
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                required
              />
            </div>

            <div>
              <label className="form-label fw-semibold small text-muted">Attachment (Optional)</label>
              <input type="file" className="form-control form-control-sm" onChange={(e) => setAttachment(e.target.files[0])} />
            </div>

            <button type="submit" className="primary__btn py-3 w-100 mt-2 border-0" disabled={isFormLoading} style={{ borderRadius: '12px' }}>
              {isFormLoading ? (
                <div className="spinner-border spinner-border-sm text-light" role="status">
                  <span className="visually-hidden">Submitting...</span>
                </div>
              ) : (
                <span>Submit Ticket</span>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Support;